import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Button, Chip, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography,
} from '@mui/material';
import { format } from 'date-fns';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import io from 'socket.io-client';
import DispatchService from '../../setting/dispatch/DispatchService';
import DispatchTaskModal from './DispatchTaskModal';
import { reasonOf, MODE } from './dispatchReasons';

// Mercari dispatch bot tasks on the admin board: the jobs that need a person. Opening a
// task claims it on the central case_claims table ("กำลังทำโดย X"), so two admins never
// act on the same job; closing the dialog without finishing releases it. Refreshes on the
// 'dispatch' case_claim_updated socket event and every minute as a fallback.

const POLL_MS = 60 * 1000;
const errorText = (err, fallback) => err?.response?.data?.message || fallback;

export default function DispatchAlertsSection() {
  const [alerts, setAlerts] = useState([]);
  const [open, setOpen] = useState(null); // the job in the dialog
  // The board shows one shop at a time — same scope as the rest of the Mercari admin page.
  const shopName = useSelector((state) => state.shop.name);

  const load = useCallback(async () => {
    try {
      const res = await DispatchService.getAlerts(shopName || null);
      setAlerts(res.data.alerts || []);
    } catch (err) {
      // Admins without the bot see nothing; a transient failure keeps the last list.
    }
  }, [shopName]);

  useEffect(() => {
    load();
    const id = setInterval(load, POLL_MS);
    const socket = io(`${process.env.REACT_APP_BACKEND_URL}/admin-dashboard`, { withCredentials: true });
    socket.on('case_claim_updated', ({ caseType } = {}) => { if (caseType === 'dispatch') load(); });
    return () => { clearInterval(id); socket.disconnect(); };
  }, [load]);

  // Open = claim. Held by someone else → ask before taking it over.
  const openTask = async (job) => {
    try {
      if (job.claimedByUserId && !job.claimedByMe) {
        if (!window.confirm(`${job.claimedByName || 'แอดมินอีกคน'} กำลังทำงานนี้อยู่ — รับต่อจากเขาไหม?`)) return;
        await DispatchService.claimJob(job.id, true);
      } else {
        await DispatchService.claimJob(job.id);
      }
      setOpen({ ...job, claimedByMe: true });
    } catch (err) {
      if (err?.response?.status === 409) {
        toast.error(`${err.response.data?.claimedByName || 'แอดมินอีกคน'} เพิ่งรับงานนี้ไป`);
        load();
      } else {
        toast.error(errorText(err, 'เปิดงานไม่สำเร็จ'));
      }
    }
  };

  // Close without finishing = let go, so others see it free again.
  const closeTask = async () => {
    const job = open;
    setOpen(null);
    if (job) {
      try { await DispatchService.releaseJob(job.id); } catch { /* already gone */ }
      load();
    }
  };

  // A finishing action: the backend clears the claim with the task.
  const act = (fn, done) => async (job) => {
    try {
      await fn(job.id);
      toast.success(done);
      setOpen(null);
      await load();
    } catch (err) {
      toast.error(errorText(err, 'เกิดข้อผิดพลาด กรุณาลองใหม่'));
    }
  };

  // กลุ่มกด: the card that won when the job is done (the proof's card), else the card the bot is
  // waiting on. The current card is the LAST one sent, which is a different buyer whenever an
  // earlier buyer's proof came in after their turn passed (owner 2026-11-02: Nam bought, Frong shown).
  const buyerOf = (job) => {
    const won = (job.offers || []).find((o) => o.status === 'done');
    return won?.orderer?.label || job.currentOffer?.orderer?.label || '—';
  };

  if (alerts.length === 0) return null;

  return (
    <Box mt={3}>
      <Box display="flex" alignItems="center" gap={1} mb={1}>
        <Typography variant="h6">งานกด Mercari (บอท)</Typography>
        <Chip size="small" color="error" label={alerts.length} />
        <Typography variant="body2" color="text.secondary">งานที่บอทส่งต่อให้แอดมิน · กดดูเพื่อรับงาน</Typography>
      </Box>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>เวลา</TableCell>
              <TableCell>เรื่อง</TableCell>
              <TableCell>ออเดอร์</TableCell>
              <TableCell>ราคา</TableCell>
              <TableCell>กลุ่มกด</TableCell>
              <TableCell>ผู้รับงาน</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {alerts.map((job) => {
              const reason = reasonOf(job);
              const order = job.order || {};
              const price = (Number(order.amount) || 0) * Math.max(1, Number(order.qty) || 1);
              return (
                // Loud (pinned buyer silent 30+ min): red row so it stands out from the rest of the board.
                <TableRow key={job.id} hover sx={reason.loud ? { bgcolor: '#ffebee', outline: '2px solid #d32f2f' } : job.claimedByMe ? { bgcolor: 'action.hover' } : undefined}>
                  <TableCell>{job.needsAdminAt ? format(new Date(job.needsAdminAt), 'd MMM HH:mm') : '—'}</TableCell>
                  <TableCell>
                    <Chip size="small" color={reason.color} label={reason.loud ? `🚨 ${reason.label}` : reason.label} sx={reason.loud ? { fontWeight: 800 } : undefined} />
                    {job.shopItem && <Chip size="small" variant="outlined" color="warning" label="Shops" sx={{ ml: 0.5 }} />}
                  </TableCell>
                  <TableCell>#{order.id} {order.smileNameInput || ''} · {MODE[job.mode] || job.mode}</TableCell>
                  <TableCell>¥{price.toLocaleString()}</TableCell>
                  <TableCell>{buyerOf(job)}</TableCell>
                  <TableCell>
                    {job.claimedByMe ? <Chip size="small" color="success" label="คุณกำลังทำ" />
                      : job.claimedByName ? <Chip size="small" label={`กำลังทำโดย ${job.claimedByName}`} />
                        : <Typography variant="caption" color="text.secondary">ยังไม่มีใครรับ</Typography>}
                  </TableCell>
                  <TableCell align="right">
                    <Button size="small" variant={job.claimedByName && !job.claimedByMe ? 'outlined' : 'contained'} onClick={() => openTask(job)}>
                      {job.claimedByMe ? 'ทำต่อ' : job.claimedByName ? 'ดู / รับต่อ' : 'รับงาน'}
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <DispatchTaskModal
        job={open}
        onClose={closeTask}
        onAcknowledge={act(DispatchService.acknowledge, 'บันทึกว่าจัดการแล้ว')}
        onInformed={act(DispatchService.informed, 'ปิดงานแล้ว — แจ้งลูกค้าเรียบร้อย')}
        onCancelOrder={act(DispatchService.cancelOrder, 'ซื้อไม่ทัน กรุณาดำเนินการต่อในหน้า ดำเนินการยกเลิก')}
        onRefundPurchase={act(DispatchService.refundPurchase, 'คืนเงินลูกค้าแล้ว — ปิดงาน')}
        onPass={act(DispatchService.passToNext, 'ปิดการ์ดของเขาแล้ว ส่งให้คนถัดไป')}
        onDefer={act(DispatchService.deferToMorning, 'เลื่อนแล้ว บอทจะส่งการ์ดใหม่ตอน 07:00')}
        onForceSend={async (job, buyerId) => {
          try {
            await DispatchService.forceSend(job.id, buyerId);
            toast.success('ส่งการ์ดแล้ว — งานนี้เป็นของคนกดคนนั้น');
            setOpen(null);
            await load();
          } catch (err) {
            toast.error(errorText(err, 'ส่งการ์ดไม่สำเร็จ'));
          }
        }}
        onProof={async (job, file, buyerId) => {
          try {
            await DispatchService.postJobProof(job.id, file, buyerId);
            toast.success('บันทึกหลักฐานแล้ว — ออเดอร์เป็นของคนกดที่เลือก');
            setOpen(null);
            await load();
          } catch (err) {
            toast.error(errorText(err, 'แนบหลักฐานไม่สำเร็จ'));
          }
        }}
      />
    </Box>
  );
}
